// KnockoutChampionsModal.js
// Web port of KnockoutChampionsModal.swift — the per-pool winners modal opened
// from the trophy button on the Knockout overview.
//
//   • Static "KNOCKOUT CHAMPIONS" title (no daily/monthly toggle).
//   • A pool-number selector ("KNOCKOUT #1", "#2", …) over chevrons — the set of
//     pool numbers that have at least one completed pool across any sport.
//   • One row per sport: winner username + sport + the round they won on, or
//     "IN PROGRESS" (the pool number exists but this sport hasn't finished it),
//     or "No data available" (no completed pools at all).
//
// Loads completed pools for all five sports on open (five concurrent reads).

import React, { useState, useEffect } from "react";
import wordmark from "../assets/Rosterable.png";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import { loadAllCompletedPools } from "../managers/knockoutService";

const SPORTS = [
  { id: "football", name: "Football", hex: "#00b04f", icon: footballIcon },
  { id: "baseball", name: "Baseball", hex: "#db2626", icon: baseballIcon },
  { id: "basketball", name: "Basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey", name: "Hockey", hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer", name: "Soccer", hex: "#1a66e6", icon: soccerIcon },
];

export default function KnockoutChampionsModal({ onClose }) {
  const [completedPools, setCompletedPools] = useState({}); // { sportId: [pools] }
  const [selectedPoolNumber, setSelectedPoolNumber] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadAllCompletedPools()
      .then((map) => {
        if (cancelled) return;
        setCompletedPools(map);
        const numbers = poolNumbersFrom(map);
        if (numbers.length > 0) setSelectedPoolNumber(numbers[numbers.length - 1]);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const availablePoolNumbers = poolNumbersFrom(completedPools);
  const hasData = availablePoolNumbers.length > 0;
  const idx = availablePoolNumbers.indexOf(selectedPoolNumber);
  const canGoBack = idx > 0;
  const canGoForward = idx >= 0 && idx < availablePoolNumbers.length - 1;

  function navigate(delta) {
    if (idx < 0) return;
    const next = idx + delta;
    if (next < 0 || next >= availablePoolNumbers.length) return;
    setSelectedPoolNumber(availablePoolNumbers[next]);
  }

  function poolFor(sportId) {
    return (completedPools[sportId] || []).find((p) => p.poolNumber === selectedPoolNumber) || null;
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.title}>KNOCKOUT CHAMPIONS</div>

        {/* Pool-number selector */}
        {hasData ? (
          <div style={styles.picker}>
            <button
              style={{ ...styles.chev, opacity: canGoBack ? 1 : 0.2, cursor: canGoBack ? "pointer" : "default" }}
              onClick={() => navigate(-1)}
              disabled={!canGoBack}
            >
              ‹
            </button>
            <span style={styles.pickerLabel}>KNOCKOUT #{selectedPoolNumber}</span>
            <button
              style={{ ...styles.chev, opacity: canGoForward ? 1 : 0.2, cursor: canGoForward ? "pointer" : "default" }}
              onClick={() => navigate(1)}
              disabled={!canGoForward}
            >
              ›
            </button>
          </div>
        ) : (
          <div style={styles.pickerEmpty}>
            {loading ? "Loading…" : "No completed pools yet"}
          </div>
        )}

        {/* Winner rows */}
        <div style={styles.rows}>
          {SPORTS.map((sport) => (
            <WinnerRow key={sport.id} sport={sport} pool={poolFor(sport.id)} hasData={hasData} />
          ))}
        </div>
      </div>
    </div>
  );
}

function WinnerRow({ sport, pool, hasData }) {
  return (
    <div style={{
      ...styles.row,
      border: `1px solid ${pool ? `${sport.hex}4d` : "rgba(255,255,255,0.06)"}`,
    }}>
      {/* Angled sport panel */}
      <div style={{ ...styles.slash, background: sport.hex }}>
        <span style={{
          ...styles.slashIcon,
          WebkitMaskImage: `url(${sport.icon})`,
          maskImage: `url(${sport.icon})`,
        }} />
      </div>

      {pool ? (
        <>
          <div style={styles.rowText}>
            <span style={styles.winnerName}>{pool.winnerUsername || "—"}</span>
            <span style={styles.sportName}>{sport.name}</span>
          </div>
          <div style={{ ...styles.roundsBadge, background: `${sport.hex}1f` }}>
            <span style={{ ...styles.roundsNum, color: sport.hex }}>{pool.currentDay}</span>
            <span style={{ ...styles.roundsLabel, color: `${sport.hex}b3` }}>ROUNDS</span>
          </div>
        </>
      ) : (
        <div style={styles.rowText}>
          <span style={styles.statusName}>{hasData ? "IN PROGRESS" : "No data available"}</span>
          <span style={styles.sportNameDim}>{sport.name}</span>
        </div>
      )}
    </div>
  );
}

// Unique pool numbers (ascending) that have at least one completed pool anywhere.
function poolNumbersFrom(map) {
  const set = new Set();
  Object.values(map || {}).forEach((pools) =>
    (pools || []).forEach((p) => set.add(p.poolNumber))
  );
  return Array.from(set).sort((a, b) => a - b);
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1000,
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#1c2130",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    padding: "16px 20px 10px",
  },
  wordmark: { height: "20px", width: "auto" },
  closeBtn: {
    position: "absolute",
    right: "16px",
    top: "14px",
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.6)",
    fontSize: "13px",
    cursor: "pointer",
  },
  title: {
    fontSize: "16px",
    fontWeight: "900",
    color: "#fff",
    letterSpacing: "1.2px",
    textAlign: "center",
    paddingBottom: "10px",
  },
  picker: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    padding: "0 20px 12px",
  },
  chev: { background: "none", border: "none", color: "#fff", fontSize: "24px", lineHeight: 1, padding: "0 4px" },
  pickerLabel: { fontSize: "14px", fontWeight: "700", color: "#fff", letterSpacing: "0.5px", minWidth: "140px", textAlign: "center" },
  pickerEmpty: { fontSize: "13px", fontWeight: "500", color: "rgba(255,255,255,0.35)", textAlign: "center", padding: "12px 20px 16px" },
  rows: { display: "flex", flexDirection: "column", gap: "10px", padding: "0 24px 24px" },
  row: {
    display: "flex",
    alignItems: "center",
    height: "52px",
    background: "#12161f",
    borderRadius: "12px",
    overflow: "hidden",
  },
  slash: {
    width: "48px",
    height: "52px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    clipPath: "polygon(0 0, 100% 0, 60% 100%, 0 100%)",
  },
  slashIcon: {
    width: "22px",
    height: "22px",
    background: "rgba(0,0,0,0.85)",
    transform: "translateX(-3px)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },
  rowText: { display: "flex", flexDirection: "column", gap: "1px", flex: 1, minWidth: 0, paddingLeft: "10px" },
  winnerName: { fontSize: "16px", fontWeight: "700", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sportName: { fontSize: "10px", fontWeight: "500", color: "rgba(255,255,255,0.4)", letterSpacing: "0.2px" },
  statusName: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px" },
  sportNameDim: { fontSize: "10px", fontWeight: "500", color: "rgba(255,255,255,0.25)", letterSpacing: "0.2px" },
  roundsBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "58px",
    height: "34px",
    borderRadius: "8px",
    marginRight: "10px",
    flexShrink: 0,
  },
  roundsNum: { fontSize: "17px", fontWeight: "800", lineHeight: 1 },
  roundsLabel: { fontSize: "8px", fontWeight: "800", letterSpacing: "0.5px" },
};
